import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ScareLogo } from "../components/Brand";
import { Check, CreditCard, Calendar, Palette, ArrowRight } from "lucide-react";

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");

export default function OnboardingPage() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: "", slug: "" });
  const [slugEdited, setSlugEdited] = useState(false);
  const [avail, setAvail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stripeBusy, setStripeBusy] = useState(false);
  const [err, setErr] = useState("");
  const mounted = useRef(false);

  // If the user already has an attraction, they don't need onboarding.
  useEffect(() => {
    if (!mounted.current && user?.attraction_id) { mounted.current = true; nav("/dashboard/overview", { replace: true }); }
  }, [user, nav]);

  const checkSlug = async (v) => {
    if (v.length >= 3) {
      try { const r = await api.get(`/attractions/slug-available?slug=${encodeURIComponent(v)}`); setAvail(r.data); }
      catch { setAvail(null); }
    } else setAvail(null);
  };
  const onName = (v) => {
    const next = { ...form, name: v };
    if (!slugEdited) { next.slug = slugify(v); checkSlug(next.slug); }
    setForm(next);
  };
  const onSlug = (v) => { const s = v.toLowerCase().replace(/[^a-z0-9-]/g, ""); setSlugEdited(true); setForm({ ...form, slug: s }); checkSlug(s); };

  const submitProfile = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { await api.post("/attractions", form); setStep(2); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  const connectStripe = async () => {
    setStripeBusy(true); setErr("");
    try {
      const r = await api.get("/stripe/connect/oauth-url", { params: { origin: window.location.origin } });
      if (r.data.url) window.location.href = r.data.url;
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Payments aren't configured on the platform yet — you can connect later from Settings."); setStripeBusy(false); }
  };

  const finish = async () => { await refresh(); nav("/dashboard/overview", { replace: true }); };

  const Steps = (
    <div className="flex items-center justify-center gap-2 mb-8" data-testid="onboarding-steps">
      {[1, 2, 3].map((n) => (
        <React.Fragment key={n}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= n ? "bg-[#dc2626] text-white" : "bg-[#1a1a1a] text-[#7a7a7a]"}`}>
            {step > n ? <Check size={15} /> : n}
          </div>
          {n < 3 && <div className={`w-10 h-0.5 ${step > n ? "bg-[#dc2626]" : "bg-[#1a1a1a]"}`} />}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-xl st-card p-10 st-fade-up">
        <div className="flex justify-center mb-6"><ScareLogo size={36} /></div>
        {Steps}

        {step === 1 && (
          <form onSubmit={submitProfile} className="space-y-4" data-testid="onboarding-form">
            <h1 className="text-2xl font-bold">Set up your attraction</h1>
            <p className="text-[#8a8a8a] -mt-2 mb-2">Just the essentials to get started — you can add your address, phone and description later in Settings.</p>
            <div>
              <label className="text-sm text-[#bbb]">Attraction name</label>
              <input data-testid="onb-name" className="st-input mt-1" required value={form.name} onChange={(e) => onName(e.target.value)} />
            </div>
            <div>
              <label className="text-sm text-[#bbb]">URL slug</label>
              <div className="flex items-center gap-2 mt-1">
                <span className="st-mono text-[#7a7a7a] text-sm">scaretickets.co.uk/</span>
                <input data-testid="onb-slug" className="st-input flex-1" required minLength={3} value={form.slug} onChange={(e) => onSlug(e.target.value)} />
              </div>
              <p className="text-[12px] text-[#7a7a7a] mt-1.5" data-testid="onb-slug-helper">
                This will be your unique booking page address on ScareTickets — e.g. scaretickets.co.uk/nightmare-manor. Choose something short and memorable.
              </p>
              {avail && (
                <div data-testid="slug-status" className={`text-xs mt-1 ${avail.available ? "text-[#86efac]" : "text-[#fca5a5]"}`}>
                  {avail.available ? "Available" : (avail.reason || "Already taken")}
                </div>
              )}
            </div>
            {err && <div className="text-sm text-[#fca5a5] bg-[#2a1212] border border-[#5b1919] rounded-lg p-3">{err}</div>}
            <button data-testid="onb-submit" disabled={loading || (avail && !avail.available)} className="st-btn w-full justify-center">
              {loading ? "Creating…" : "Continue"} <ArrowRight size={15} />
            </button>
          </form>
        )}

        {step === 2 && (
          <div className="text-center" data-testid="onboarding-stripe">
            <div className="inline-flex w-16 h-16 rounded-full bg-[#160a0a] items-center justify-center text-[#dc2626] mb-4"><CreditCard size={30} /></div>
            <h1 className="text-2xl font-bold">Connect your Stripe account</h1>
            <p className="text-[#bbb] mt-2 mb-6">Connect Stripe to start accepting payments. You can do this any time from Settings — but you won't be able to take payments until it's connected.</p>
            {err && <div className="text-sm text-[#e8c987] bg-[#1a1205] border border-[#3a2a0a] rounded-lg p-3 mb-4">{err}</div>}
            <button onClick={connectStripe} disabled={stripeBusy} className="st-btn w-full justify-center" data-testid="onb-connect-stripe">
              {stripeBusy ? "Redirecting…" : "Connect with Stripe"}
            </button>
            <button onClick={() => setStep(3)} className="st-link text-sm mt-4" data-testid="onb-skip-stripe">Skip for now — I'll do this later</button>
          </div>
        )}

        {step === 3 && (
          <div className="text-center" data-testid="onboarding-welcome">
            <div className="inline-flex w-16 h-16 rounded-full bg-[#0d2818] items-center justify-center text-[#22c55e] mb-4"><Check size={32} /></div>
            <h1 className="text-2xl font-bold">You're all set!</h1>
            <p className="text-[#bbb] mt-2 mb-6">Here's how to get your first event live:</p>
            <div className="space-y-2 text-left">
              <QuickStart icon={Calendar} title="Create your first event" desc="Set up open days, ticket types and add-ons." />
              <QuickStart icon={Palette} title="Customise your booking page" desc="Match your brand with the design editor." />
              <QuickStart icon={CreditCard} title="Connect Stripe to get paid" desc="Receive payouts directly to your account." />
            </div>
            <button onClick={finish} className="st-btn w-full justify-center mt-6" data-testid="onb-go-dashboard">Go to Dashboard <ArrowRight size={15} /></button>
          </div>
        )}
      </div>
    </div>
  );
}

function QuickStart({ icon: Icon, title, desc }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-[#0e0e0e] border border-[#1a1a1a]">
      <Icon size={18} className="text-[#dc2626] mt-0.5 shrink-0" />
      <div><div className="font-medium text-sm">{title}</div><div className="text-xs text-[#8a8a8a]">{desc}</div></div>
    </div>
  );
}
